import { useCallback, useEffect, useRef, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import {
  useCreateTailgateGridTestJobMutation,
  useTailgateGridTestJobQuery,
} from '@/graphql/generated/hooks'
import { TailgateGridTestJobQuery } from '@/graphql/generated/operations'
import {
  BoxInput,
  TailgateGridTestFailureReason,
  TailgateGridTestJobStatus,
} from '@/graphql/generated/schemas'

const POLL_INTERVAL_MS = 3_000
const POLL_TIMEOUT_MS = 5 * 60_000
const SENTRY_FEATURE = 'tailgate_grid_testing'

export type TailgateGridTestJobResult = NonNullable<
  TailgateGridTestJobQuery['tailgateGridTestJob']
>

interface StartTailgateGridTestJobIProps {
  /** Camera whose grid is under test; pins the backend's clip-info lookup on multi-camera doors (ENG-34041). */
  cameraId?: string | null
  incidentId: string
  securedBox: BoxInput
  unsecuredBox: BoxInput
}

interface UseTailgateGridTestJobIProps {
  isEnabled: boolean
  onCompleted?: () => void
  onFailed?: (isTimeout: boolean) => void
}

export const useTailgateGridTestJob = ({
  isEnabled,
  onCompleted,
  onFailed,
}: UseTailgateGridTestJobIProps) => {
  const onCompletedRef = useRef(onCompleted)
  const onFailedRef = useRef(onFailed)
  onCompletedRef.current = onCompleted
  onFailedRef.current = onFailed

  const hasNotifiedRef = useRef(false)
  const sessionTokenRef = useRef(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [status, setStatus] = useState<TailgateGridTestJobStatus | null>(null)
  const [isTimeout, setIsTimeout] = useState(false)
  const [error, setError] = useState<Error | undefined>()

  const [createMutation] = useCreateTailgateGridTestJobMutation()
  const {
    data,
    error: queryError,
    stopPolling,
  } = useTailgateGridTestJobQuery({
    variables: { id: jobId ?? '' },
    skip: !isEnabled || !jobId,
    fetchPolicy: 'network-only',
    pollInterval: isEnabled && jobId ? POLL_INTERVAL_MS : 0,
  })

  const job = data?.tailgateGridTestJob ?? undefined

  const clearPollTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    if (
      !jobId ||
      !job ||
      job.id !== jobId ||
      status !== TailgateGridTestJobStatus.Pending ||
      hasNotifiedRef.current
    ) {
      return
    }

    if (job.status === TailgateGridTestJobStatus.Completed) {
      hasNotifiedRef.current = true
      clearPollTimeout()
      stopPolling()
      setStatus(TailgateGridTestJobStatus.Completed)
      onCompletedRef.current?.()
      return
    }

    if (job.status === TailgateGridTestJobStatus.Failed) {
      const didTimeOut =
        job.failureReason === TailgateGridTestFailureReason.Timeout
      hasNotifiedRef.current = true
      clearPollTimeout()
      stopPolling()
      setIsTimeout(didTimeOut)
      setStatus(TailgateGridTestJobStatus.Failed)
      const failure = new Error('Tailgate grid test job failed')
      setError(failure)
      Sentry.captureException(failure, {
        tags: {
          action: 'poll_job_failed',
          feature: SENTRY_FEATURE,
        },
        extra: {
          jobId: job.id,
          jobStatus: job.status,
          failureReason: job.failureReason,
        },
      })
      onFailedRef.current?.(didTimeOut)
    }
  }, [job, jobId, status, clearPollTimeout, stopPolling])

  useEffect(() => {
    if (
      !queryError ||
      !jobId ||
      status !== TailgateGridTestJobStatus.Pending ||
      hasNotifiedRef.current
    ) {
      return
    }

    hasNotifiedRef.current = true
    clearPollTimeout()
    stopPolling()
    setStatus(TailgateGridTestJobStatus.Failed)
    setError(queryError)
    Sentry.captureException(queryError, {
      tags: {
        action: 'poll_job',
        feature: SENTRY_FEATURE,
      },
      extra: { jobId },
    })
    onFailedRef.current?.(false)
  }, [queryError, status, jobId, clearPollTimeout, stopPolling])

  const reset = useCallback(() => {
    sessionTokenRef.current += 1
    clearPollTimeout()
    stopPolling()
    setJobId(null)
    setStatus(null)
    setIsTimeout(false)
    setError(undefined)
    hasNotifiedRef.current = false
  }, [clearPollTimeout, stopPolling])

  useEffect(
    () => () => {
      sessionTokenRef.current += 1
      clearPollTimeout()
      stopPolling()
    },
    [clearPollTimeout, stopPolling]
  )

  const start = useCallback(
    async ({
      cameraId,
      incidentId,
      securedBox,
      unsecuredBox,
    }: StartTailgateGridTestJobIProps) => {
      if (!isEnabled) return

      reset()
      const sessionToken = sessionTokenRef.current
      setStatus(TailgateGridTestJobStatus.Pending)

      try {
        const result = await createMutation({
          variables: {
            // cameraId spread conditionally: callers without a camera context
            // keep the pre-ENG-34041 payload and the backend's deterministic
            // fallback.
            input: {
              incidentId,
              securedBox,
              unsecuredBox,
              ...(cameraId ? { cameraId } : {}),
            },
          },
        })
        if (sessionToken !== sessionTokenRef.current) return

        const newJobId =
          result.data?.createTailgateGridTestJob?.tailgateGridTestJob?.id
        if (!newJobId) {
          throw new Error('No job id returned from createTailgateGridTestJob')
        }

        setJobId(newJobId)
        timeoutRef.current = setTimeout(() => {
          if (
            sessionToken !== sessionTokenRef.current ||
            hasNotifiedRef.current
          ) {
            return
          }
          hasNotifiedRef.current = true
          stopPolling()
          setIsTimeout(true)
          setStatus(TailgateGridTestJobStatus.Failed)
          const timeoutError = new Error(
            `Tailgate grid test polling timed out after ${POLL_TIMEOUT_MS}ms`
          )
          setError(timeoutError)
          Sentry.captureException(timeoutError, {
            tags: {
              action: 'poll_job_timeout',
              feature: SENTRY_FEATURE,
            },
            extra: { jobId: newJobId, timeoutMs: POLL_TIMEOUT_MS },
          })
          onFailedRef.current?.(true)
        }, POLL_TIMEOUT_MS)
      } catch (cause) {
        if (sessionToken !== sessionTokenRef.current) return

        hasNotifiedRef.current = true
        const startError =
          cause instanceof Error ? cause : new Error(String(cause))
        setStatus(TailgateGridTestJobStatus.Failed)
        setError(startError)
        Sentry.captureException(startError, {
          tags: {
            action: 'create_job',
            feature: SENTRY_FEATURE,
          },
          extra: { incidentId },
        })
        onFailedRef.current?.(false)
      }
    },
    [createMutation, isEnabled, reset, stopPolling]
  )

  return { error, isTimeout, job, reset, start, status }
}
